/**
 * Custom Fields PDF Renderer
 * Converts dynamic form fields into HTML for PDF export
 */

import type { FormTemplate, PageSection, FieldSchema } from '../types/formSchema';

interface PDFRenderOptions {
  pageNumber: number;
  totalPages: number;
  generateHeader: (surveyData: any, pageNumber: number, totalPages: number, options?: any) => string;
  generateFooter?: (options?: any) => string;
  surveyData: any;
  formData: Record<string, any>;
  options?: any;
}

/**
 * Render a complete custom page for PDF
 * Groups fields by section and renders with section headers
 * Returns array of page HTML strings (may be multiple pages if photos exceed 6 per page)
 */
export function renderCustomPageForPDF(
  page: PageSection,
  renderOptions: PDFRenderOptions
): string[] {
  const { pageNumber, totalPages, generateHeader, generateFooter, surveyData, formData, options } = renderOptions;
  
  // Debug: Log what data we're working with
  console.log(`[PDF Renderer] Rendering custom page "${page.title}" (${page.id})`);
  console.log(`[PDF Renderer] Page has ${page.fields.length} fields`);
  
  // Check if page should be included in PDF
  if (!page.showInPDF) {
    return [];
  }
  
  const pages: string[] = [];
  let currentPageNum = pageNumber;
  
  // Sort fields by order
  const sortedFields = [...page.fields].sort((a, b) => a.order - b.order);
  
  // ===== STEP 1: Collect all photos from all fields =====
  const allPhotos: Array<{ url: string; label: string; fieldLabel: string }> = [];
  
  for (const field of sortedFields) {
    // Check for photo attachments (fields with enablePhotoCapture)
    if ((field as any).enablePhotoCapture) {
      const photosFieldId = `${field.id}_photos`;
      const photos = formData[photosFieldId];
      if (photos && Array.isArray(photos) && photos.length > 0) {
        photos.forEach((photo, idx) => {
          const photoUrl = typeof photo === 'string' ? photo : (photo.cdnUrl || photo.url || photo.dataUrl);
          const customCaption = typeof photo === 'object' ? (photo.caption || photo.notes || photo.note || '') : '';
          if (photoUrl) {
            allPhotos.push({
              url: photoUrl,
              label: customCaption || `${field.label} (${idx + 1}/${photos.length})`,
              fieldLabel: field.label
            });
          }
        });
      }
    }
    
    // Check for image type fields
    if (field.type === 'image') {
      const images = formData[field.id];
      if (images && Array.isArray(images) && images.length > 0) {
        images.forEach((img, idx) => {
          const imgUrl = typeof img === 'string' ? img : (img.cdnUrl || img.url || img.dataUrl);
          const caption = typeof img === 'object' ? (img.caption || img.notes || '') : '';
          if (imgUrl) {
            allPhotos.push({
              url: imgUrl,
              label: caption || `${field.label} (${idx + 1}/${images.length})`,
              fieldLabel: field.label
            });
          }
        });
      }
    }
  }
  
  console.log(`[PDF Renderer] Collected ${allPhotos.length} total photos from all fields`);
  
  // ===== STEP 2: Group fields by section =====

  // ===== Group fields by section =====
  const fieldsBySection: Record<string, FieldSchema[]> = {};
  const fieldsWithoutSection: FieldSchema[] = [];
  
  sortedFields.forEach((field) => {
    if (field.section) {
      if (!fieldsBySection[field.section]) {
        fieldsBySection[field.section] = [];
      }
      fieldsBySection[field.section].push(field);
    } else {
      fieldsWithoutSection.push(field);
    }
  });
  
  const sectionNames = Object.keys(fieldsBySection);
  // ====================================

  // Generate HTML for this page
  let html = `
    <section class="pdf-page" id="${page.id}">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      
      <div class="pdf-content">
        <!-- Styled section heading like built-in pages -->
        <div class="section-heading-wrapper">
          <div class="section-icon-bar"></div>
          <h3>${page.title || 'Custom Section'}</h3>
        </div>
        ${page.description ? `<p class="section-description">${page.description}</p>` : ''}
        
        <div class="custom-fields-container">`;

  // ===== Render fields without sections first =====
  if (fieldsWithoutSection.length > 0) {
    for (const field of fieldsWithoutSection) {
      const value = formData[field.id];
      html += renderFieldForPDF(field, value, formData);
    }
  }
  
  // ===== Render sectioned fields with headers =====
  for (const sectionName of sectionNames) {
    const sectionFields = fieldsBySection[sectionName];
    
    // Debug: Log field lookup
    console.log(`[PDF Renderer] Section "${sectionName}" fields:`, sectionFields.map(f => ({
      id: f.id,
      label: f.label,
      hasValue: formData[f.id] !== undefined && formData[f.id] !== null && formData[f.id] !== ''
    })));
    
    // Check if section has any visible content (fields with values)
    const hasVisibleFields = sectionFields.some(field => {
      const value = formData[field.id];
      return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
    });
    
    // Skip empty sections
    if (!hasVisibleFields) {
      console.log(`[PDF Renderer] Skipping empty section "${sectionName}"`);
      continue;
    }
    
    html += `
      <div class="custom-section">
        <h3 class="custom-section-header">${sectionName}</h3>
        <div class="custom-section-fields">`;
    
    for (const field of sectionFields) {
      const value = formData[field.id];
      html += renderFieldForPDF(field, value, formData);
    }
    
    html += `
        </div>
      </div>`;
  }
  // ==================================================

  html += `
        </div>
      </div>
      ${generateFooter ? generateFooter(options) : ''}
    </section>`;

  // Add main page
  pages.push(html);
  
  // ===== Generate continuation pages for fields with >6 photos =====
  currentPageNum = pageNumber + 1; // Increment from the currentPageNum declared at top
  console.log(`[PDF Renderer] Checking for continuation pages in "${page.title}"...`);
  
  for (const field of sortedFields) {
    // Handle fields with enablePhotoCapture (text, number, etc. with photos)
    if ((field as any).enablePhotoCapture) {
      const photosFieldId = `${field.id}_photos`;
      const photos = formData[photosFieldId];
      if (photos && Array.isArray(photos) && photos.length > 6) {
        // Generate continuation pages for remaining photos
        const photosPerPage = 6;
        const totalPhotoPages = Math.ceil(photos.length / photosPerPage);
        console.log(`[PDF Renderer] Field "${field.label}" has ${photos.length} photos → generating ${totalPhotoPages - 1} continuation page(s)`);
        
        // Start from page 2 (first 6 already on main page)
        for (let photoPage = 1; photoPage < totalPhotoPages; photoPage++) {
          const startIndex = photoPage * photosPerPage;
          const photoResult = renderPhotoAttachments(photos, field.label, startIndex, photosPerPage);
          
          console.log(`[PDF Renderer] Creating continuation page ${photoPage + 1}/${totalPhotoPages} for field "${field.label}" (photos ${startIndex + 1}-${Math.min(startIndex + photosPerPage, photos.length)}/${photos.length})`);
          
          const continuationPage = `
            <section class="pdf-page" id="${page.id}_photos_${field.id}_${photoPage}">
              ${generateHeader(surveyData, currentPageNum, totalPages, options)}
              
              <div class="pdf-content">
                <div class="section-heading-wrapper">
                  <div class="section-icon-bar"></div>
                  <h3>${page.title}: ${field.label} (${photoPage + 1}/${totalPhotoPages})</h3>
                </div>
                ${photoResult.html}
              </div>
              ${generateFooter ? generateFooter(options) : ''}
            </section>`;
          
          pages.push(continuationPage);
          console.log(`[PDF Renderer] Pushed continuation page to array (${pages.length} total pages now)`);
          currentPageNum++;
        }
      }
    }
    
    // Handle image type fields with >6 images
    if (field.type === 'image') {
      const images = formData[field.id];
      if (images && Array.isArray(images) && images.length > 6) {
        const imagesPerPage = 6;
        const totalImagePages = Math.ceil(images.length / imagesPerPage);
        console.log(`[PDF Renderer] Image field "${field.label}" has ${images.length} images → generating ${totalImagePages - 1} continuation page(s)`);
        
        // Start from page 2 (first 6 already on main page)
        for (let imagePage = 1; imagePage < totalImagePages; imagePage++) {
          const startIndex = imagePage * imagesPerPage;
          const endIndex = Math.min(startIndex + imagesPerPage, images.length);
          const pageImages = images.slice(startIndex, endIndex);
          
          console.log(`[PDF Renderer] Creating continuation page ${imagePage + 1}/${totalImagePages} for image field "${field.label}" (images ${startIndex + 1}-${endIndex}/${images.length})`);
          
          let imageGridHtml = '<div class="property-photo-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12pt; margin-top: 12pt;">';
          
          pageImages.forEach((img: any, idx: number) => {
            const globalIndex = startIndex + idx;
            const imgUrl = typeof img === 'string' ? img : (img.cdnUrl || img.url || img.dataUrl);
            const caption = typeof img === 'object' ? (img.caption || img.notes || '') : '';
            const photoNumber = globalIndex + 1;
            
            if (!imgUrl) return;
            
            const labelText = caption ? `${caption} (${photoNumber}/${images.length})` : `Image ${photoNumber}/${images.length}`;
            
            imageGridHtml += `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img 
                    src="${escapeHtml(imgUrl)}" 
                    alt="${escapeHtml(labelText)}" 
                  />
                </div>
                <div class="property-photo-label">${escapeHtml(labelText)}</div>
              </div>`;
          });
          
          imageGridHtml += '</div>';
          
          const continuationPage = `
            <section class="pdf-page" id="${page.id}_images_${field.id}_${imagePage}">
              ${generateHeader(surveyData, currentPageNum, totalPages, options)}
              
              <div class="pdf-content">
                <div class="section-heading-wrapper">
                  <div class="section-icon-bar"></div>
                  <h3>${page.title}: ${field.label} (${imagePage + 1}/${totalImagePages})</h3>
                </div>
                ${imageGridHtml}
              </div>
              ${generateFooter ? generateFooter(options) : ''}
            </section>`;
          
          pages.push(continuationPage);
          currentPageNum++;
        }
      }
    }
  }
  
  console.log(`✅ [PDF Renderer] Generated ${pages.length} page(s) for custom page "${page.title}":`);
  console.log(`   - Main page: 1`);
  console.log(`   - Continuation pages: ${pages.length - 1}`);
  console.log(`   - All pages have proper <section class="pdf-page"> wrappers`);
  return pages;
}

/**
 * Render a single field for PDF
 * Exported for use in built-in section renderers
 */
export function renderFieldForPDF(field: FieldSchema, value: any, formData?: Record<string, any>): string {
  // Skip fields with no value unless they're required
  if (!value && !field.required && value !== 0 && value !== false) {
    // Check if field has photos even if main value is empty
    const hasPhotos = formData && (field as any).enablePhotoCapture && formData[`${field.id}_photos`]?.length > 0;
    if (!hasPhotos) return '';
  }

  // Determine if this field has photos
  const hasPhotos = field.type === 'image' || 
                    (formData && (field as any).enablePhotoCapture && formData[`${field.id}_photos`]?.length > 0);
  const photoFieldClass = hasPhotos ? ' photo-field' : '';
  
  let html = `
    <div class="field-group${photoFieldClass}">
      <div class="field-label">
        ${field.label}${field.required ? '<span class="required">*</span>' : ''}
        ${field.type === 'number' && field.unit ? ` <span class="unit">(${field.unit})</span>` : ''}
      </div>`;

  // Render based on field type
  switch (field.type) {
    case 'text':
    case 'textarea':
      html += renderTextField(value);
      break;
    
    case 'number':
      html += renderNumberField(value, field);
      break;
    
    case 'date':
      html += renderDateField(value);
      break;
    
    case 'dropdown':
      html += renderDropdownField(value, field);
      break;
    
    case 'multiChoice':
      html += renderMultiChoiceField(value, field);
      break;
    
    case 'boolean':
      html += renderBooleanField(value);
      break;
    
    case 'image':
      html += renderImageField(value, field);
      break;
    
    default:
      html += renderTextField(value);
  }
  
  html += `
    </div>`;

  return html;
}

/**
 * Render text field
 */
function renderTextField(value: any): string {
  if (!value) return '<div class="field-value empty">—</div>';
  
  const textValue = String(value);
  // Check if it's a long text that needs multi-line display
  if (textValue.length > 100 || textValue.includes('\n')) {
    return `<div class="field-value multiline">${escapeHtml(textValue).replace(/\n/g, '<br>')}</div>`;
  }
  
  return `<div class="field-value">${escapeHtml(textValue)}</div>`;
}

/**
 * Render number field
 */
function renderNumberField(value: any, field: FieldSchema & { type: 'number' }): string {
  if (value === null || value === undefined || value === '') {
    return '<div class="field-value empty">—</div>';
  }
  
  const numValue = Number(value);
  const formatted = field.unit ? `${numValue} ${field.unit}` : String(numValue);
  
  return `<div class="field-value">${escapeHtml(formatted)}</div>`;
}

/**
 * Render date field
 */
function renderDateField(value: any): string {
  if (!value) return '<div class="field-value empty">—</div>';
  
  try {
    const date = new Date(value);
    const formatted = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return `<div class="field-value">${formatted}</div>`;
  } catch {
    return `<div class="field-value">${escapeHtml(String(value))}</div>`;
  }
}

/**
 * Render dropdown field
 */
function renderDropdownField(value: any, field: FieldSchema & { type: 'dropdown' }): string {
  if (!value) return '<div class="field-value empty">—</div>';
  
  // Find the label for the selected value
  const option = field.options?.find(opt => opt.value === value);
  const displayText = option ? option.label : String(value);
  
  return `<div class="field-value">${escapeHtml(displayText)}</div>`;
}

/**
 * Render multi-choice field
 */
function renderMultiChoiceField(value: any, field: FieldSchema & { type: 'multiChoice' }): string {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return '<div class="field-value empty">—</div>';
  }
  
  const values = Array.isArray(value) ? value : [value];
  const labels = values.map(val => {
    const option = field.options?.find(opt => opt.value === val);
    return option ? option.label : String(val);
  });
  
  return `
    <div class="field-value">
      <ul class="choice-list">
        ${labels.map(label => `<li>✓ ${escapeHtml(label)}</li>`).join('')}
      </ul>
    </div>`;
}

/**
 * Render boolean field
 */
function renderBooleanField(value: any): string {
  if (value === null || value === undefined) {
    return '<div class="field-value">N/A</div>';
  }
  
  const displayText = value ? 'Yes' : 'No';
  const className = value ? 'boolean-yes' : 'boolean-no';
  
  return `<div class="field-value ${className}">${displayText}</div>`;
}

/**
 * Render image field
 * ONLY renders first 6 images on main page
 * Continuation pages are handled by renderCustomPageForPDF
 */
function renderImageField(value: any, field: FieldSchema & { type: 'image' }): string {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return '<div class="field-value empty">No images</div>';
  }
  
  const maxPerPage = 6; // Always enforce 6 per page
  const imagesPerRow = 3; // 3 columns = 2 rows of 3 for 6 images
  
  // Only render first 6 images on main page
  const imagesToRender = value.slice(0, maxPerPage);
  const totalImages = value.length;
  
  let html = '<div class="field-value">';
  
  html += `<div class="property-photo-grid" style="display: grid; grid-template-columns: repeat(${imagesPerRow}, 1fr); gap: 12pt; margin-top: 12pt;">`;
  
  imagesToRender.forEach((img, idx) => {
    const imgUrl = typeof img === 'string' ? img : (img.cdnUrl || img.url || img.dataUrl);
    const caption = typeof img === 'object' ? (img.caption || img.notes || '') : '';
    const photoNumber = idx + 1;
    
    if (!imgUrl) return;
    
    const labelText = caption ? `${caption} (${photoNumber}/${totalImages})` : `Image ${photoNumber}/${totalImages}`;
    
    html += `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img 
            src="${escapeHtml(imgUrl)}" 
            alt="${escapeHtml(labelText)}" 
          />
        </div>
        <div class="property-photo-label">${escapeHtml(labelText)}</div>
      </div>`;
  });
  
  html += '</div>';
  
  if (totalImages > maxPerPage) {
    html += `<p style="color: #6b7280; font-size: 9pt; margin-top: 8pt; font-style: italic;">Showing ${maxPerPage} of ${totalImages} images. Additional images on next page(s).</p>`;
  }
  
  html += '</div>';
  
  return html;
}

/**
 * Render photo attachments from fields with enablePhotoCapture
 * Renders up to 6 photos in a 3-column grid matching built-in page photo layouts
 * Returns object with HTML and flag indicating if there are more photos
 */
function renderPhotoAttachments(photos: any[], fieldLabel: string, startIndex: number = 0, maxPhotos: number = 6): { html: string; hasMore: boolean; nextIndex: number } {
  if (!photos || !Array.isArray(photos) || photos.length === 0) {
    return { html: '', hasMore: false, nextIndex: 0 };
  }
  
  const totalPhotos = photos.length;
  const endIndex = Math.min(startIndex + maxPhotos, totalPhotos);
  const photosToRender = photos.slice(startIndex, endIndex);
  const hasMore = endIndex < totalPhotos;
  
  let html = '<div class="property-photo-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12pt; margin-top: 12pt;">';
  
  photosToRender.forEach((photo, idx) => {
    const globalIndex = startIndex + idx;
    // Handle different photo data formats
    const photoUrl = typeof photo === 'string' ? photo : (photo.cdnUrl || photo.url || photo.dataUrl);
    const customCaption = typeof photo === 'object' ? (photo.caption || photo.notes || photo.note || '') : '';
    const photoNumber = globalIndex + 1;
    
    if (!photoUrl) return;
    
    // Create label text matching built-in pages format:
    // If custom caption: use it with counter
    // Otherwise: "{Field Label} Evidence (1/3)"
    let labelText: string;
    if (customCaption) {
      labelText = `${customCaption} (${photoNumber}/${totalPhotos})`;
    } else {
      // Use field label for context, e.g., "Site Photos Evidence (1/3)"
      const baseLabel = fieldLabel.replace(/photos?$/i, '').trim() || fieldLabel;
      labelText = `${baseLabel} Evidence (${photoNumber}/${totalPhotos})`;
    }
    
    html += `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img 
            src="${escapeHtml(photoUrl)}" 
            alt="Photo ${photoNumber}" 
          />
        </div>
        <div class="property-photo-label">${escapeHtml(labelText)}</div>
      </div>`;
  });
  
  html += '</div>';
  
  return { html, hasMore, nextIndex: endIndex };
}

/**
 * Calculate how many PDF pages a custom page will take
 * (mainly for images that span multiple pages)
 */
export function calculateCustomPageCount(
  page: PageSection,
  formData: Record<string, any>
): number {
  if (!page.showInPDF) {
    return 0;
  }
  
  let pageCount = 1; // Start with 1 for the main content
  
  // Check for image fields and photo attachments that might require additional pages
  for (const field of page.fields) {
    if (field.type === 'image') {
      const value = formData[field.id];
      if (value && Array.isArray(value) && value.length > 0) {
        const maxPerPage = 6; // Always 6 per page
        const imagePages = Math.ceil(value.length / maxPerPage);
        
        // If images require more than 1 page, add continuation pages
        if (imagePages > 1) {
          pageCount += (imagePages - 1);
          console.log(`[Page Count] Image field "${field.label}" has ${value.length} images → +${imagePages - 1} continuation page(s)`);
        }
      }
    }
    
    // Check for photo attachments (enablePhotoCapture fields)
    if ((field as any).enablePhotoCapture) {
      const photosFieldId = `${field.id}_photos`;
      const photos = formData[photosFieldId];
      if (photos && Array.isArray(photos) && photos.length > 0) {
        const photosPerPage = 6;
        const photoPages = Math.ceil(photos.length / photosPerPage);
        
        // If photos require more than 1 page, add continuation pages
        if (photoPages > 1) {
          pageCount += (photoPages - 1);
          console.log(`[Page Count] Photo field "${field.label}" has ${photos.length} photos → +${photoPages - 1} continuation page(s)`);
        }
      }
    }
  }
  
  console.log(`[Page Count] Custom page "${page.title}" total: ${pageCount} page(s)`);
  
  return pageCount;
}

/**
 * Get all custom pages from template that should be in PDF
 * Handles both old format (pages) and new format (customPages)
 */
export function getCustomPagesForPDF(template: FormTemplate): PageSection[] {
  // Check for new format first (customPages)
  if (template.customPages && template.customPages.length > 0) {
    console.log('[PDF] Using customPages format:', template.customPages.length);
    console.log('[PDF] Raw customPages:', template.customPages.map(cp => ({
      id: cp.id,
      label: cp.label,
      fieldsCount: cp.fields?.length || 0,
      showInPDF: cp.showInPDF,
      fields: cp.fields
    })));
    
    // Convert CustomPage to PageSection format and filter by showInPDF
    return template.customPages
      .filter(customPage => customPage.showInPDF !== false) // Default to true if not specified
      .map(customPage => ({
        id: customPage.id,
        title: customPage.label || 'Untitled Page',
        fields: customPage.fields || [],
        showInPDF: customPage.showInPDF !== false, // Respect the flag, default to true
        icon: customPage.icon,
        order: customPage.order || 0
      }));
  }
  
  // Fall back to old format (pages)
  if (template.pages && template.pages.length > 0) {
    console.log('[PDF] Using pages format:', template.pages.length);
    return template.pages.filter(page => page.showInPDF !== false);
  }
  
  console.log('[PDF] No custom pages found');
  return [];
}

/**
 * Calculate total page count for all custom pages
 */
export function calculateTotalCustomPagesCount(
  template: FormTemplate,
  formData: Record<string, any>
): number {
  const customPages = getCustomPagesForPDF(template);
  
  return customPages.reduce((total, page) => {
    return total + calculateCustomPageCount(page, formData);
  }, 0);
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Generate CSS styles for custom fields in PDF
 */
export function getCustomFieldsPDFStyles(): string {
  return `
    /* Section heading with icon bar (matching built-in pages) */
    .section-heading-wrapper {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
    }
    
    .section-icon-bar {
      width: 6px;
      height: 24px;
      background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
      border-radius: 3px;
      margin-right: 12px;
      flex-shrink: 0;
    }
    
    .section-heading-wrapper h3 {
      margin: 0;
      font-size: 14pt;
      font-weight: 600;
      color: #1f2937;
    }
    
    .custom-fields-container {
      margin-top: 20px;
    }
    
    .field-group {
      margin-bottom: 16px;
      page-break-inside: avoid;
    }
    
    /* Allow page breaks within field groups that contain photo grids */
    .field-group:has(.property-photo-grid),
    .field-group.photo-field {
      page-break-inside: auto;
    }
    
    .field-label {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 6px;
      font-size: 11px;
    }
    
    .field-label .required {
      color: #ef4444;
      margin-left: 2px;
    }
    
    .field-label .unit {
      color: #6b7280;
      font-weight: 400;
    }
    
    .field-value {
      background-color: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      padding: 8px 12px;
      font-size: 10px;
      color: #374151;
      min-height: 32px;
    }
    
    /* Allow field values with photo grids to break across pages */
    .field-value:has(.property-photo-grid),
    .field-group.photo-field .field-value {
      page-break-inside: auto;
      background-color: transparent;
      border: none;
      padding: 0;
    }
    
    .field-value.empty {
      color: #9ca3af;
      font-style: italic;
    }
    
    .field-value.multiline {
      white-space: pre-wrap;
      line-height: 1.5;
    }
    
    .field-value.boolean-yes {
      color: #059669;
      font-weight: 600;
    }
    
    .field-value.boolean-no {
      color: #dc2626;
      font-weight: 600;
    }
    
    .choice-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    
    .choice-list li {
      padding: 4px 0;
      color: #059669;
    }
    
    .images-grid {
      display: grid;
      gap: 10px;
      margin-top: 10px;
    }
    
    /* Allow photo grids to break across pages */
    .property-photo-grid {
      page-break-inside: auto;
    }
    
    /* Allow individual photo wrappers to break if needed */
    .property-photo-wrapper {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    .image-item {
      display: flex;
      flex-direction: column;
    }
    
    .image-caption {
      font-size: 10px;
      color: #666;
      margin-top: 4px;
      text-align: center;
    }
    
    .section-description {
      color: #6b7280;
      font-size: 10px;
      margin-bottom: 16px;
      font-style: italic;
    }
    
    .page-break {
      page-break-before: always;
    }
    
    /* Section grouping styles */
    .custom-section {
      margin-bottom: 24px;
      page-break-inside: avoid;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 16px;
      background-color: #ffffff;
    }
    
    .custom-section-header {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
      border-bottom: 2px solid #3b82f6;
      padding-bottom: 8px;
      margin-bottom: 16px;
      margin-top: 0;
    }
    
    .custom-section-fields {
      display: grid;
      grid-template-columns: 1fr;
      gap: 12px;
    }
    
    /* Tighten spacing for fields within sections */
    .custom-section .field-group {
      margin-bottom: 12px;
    }
    
    /* Last field in section has no bottom margin */
    .custom-section .field-group:last-child {
      margin-bottom: 0;
    }
  `;
}
